import React, { useState } from 'react';
import { useField } from 'formik';
import { FormikRadioGroup } from './FormikRadioGroup';
import { Box, MenuItem, TextField } from '@material-ui/core';
import type { FormField } from '../interfaces';
import {
    findCamundaPropertyValue,
    findCamundaValidation,
    getLocalizedLabel,
} from '../utils';
import { useGetDynamicFormDataQuery } from '../services/rtkApi';
import { Loader } from '@klp/design/Loader';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

export const NyProsjektlederSelector = ({
    isRequired,
    isReadOnly,
    field,
    fields,
    locale,
}: {
    isRequired?: boolean;
    isReadOnly?: boolean;
    field?: FormField;
    fields?: Array<FormField>;
    locale: string;
}) => {
    const classes = getFormFieldStyles();
    const [prosjektlederBekreftelseFormikField] = useField(field?.id as string);
    const [nyProsjektlederFormikField, nyProsjektlederFormikMeta] =
        useField('nyProsjektleder');

    const nyProsjektlederField = fields?.find(
        (f) => f.id === nyProsjektlederFormikField?.name,
    );

    const [showConditionalField, setShowConditionalField] = useState(undefined);

    const nyProsjektlederIsRequired = findCamundaValidation(
        'required',
        nyProsjektlederField?.validation,
    );

    const nyProsjektlederApiProperty = findCamundaPropertyValue(
        'api',
        nyProsjektlederField?.properties,
    );

    const { data, isLoading: getDynamicFormDataIsLoading } =
        useGetDynamicFormDataQuery(
            { endpoint: nyProsjektlederApiProperty || '' },
            {
                skip: !nyProsjektlederApiProperty,
            },
        );

    return (
        <Box className={classes.container}>
            <FormikRadioGroup
                {...prosjektlederBekreftelseFormikField}
                id={field?.id}
                label={getLocalizedLabel(
                    `project.formFields.${field?.id}`,
                    field?.label,
                    field?.id,
                )}
                isRequired={isRequired}
                isReadOnly={isReadOnly}
                callback={setShowConditionalField}
                locale={locale}
            />
            {showConditionalField === false ||
            prosjektlederBekreftelseFormikField?.value === 'false' ||
            (nyProsjektlederFormikMeta.touched &&
                Boolean(nyProsjektlederFormikMeta.error)) ? (
                <Loader isLoading={getDynamicFormDataIsLoading}>
                    <Box paddingTop="15px">
                        <TextField
                            select={true}
                            fullWidth={true}
                            className={classNames({
                                [classes.editableField]: !isReadOnly,
                                [classes.readOnlyField]: isReadOnly,
                            })}
                            required={nyProsjektlederIsRequired}
                            variant="outlined"
                            {...nyProsjektlederFormikField}
                            id={nyProsjektlederField?.id}
                            defaultValue={
                                nyProsjektlederField?.value ??
                                nyProsjektlederField?.defaultValue
                            }
                            disabled={!!isReadOnly}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            label={getLocalizedLabel(
                                `project.formFields.${nyProsjektlederField?.id}`,
                                nyProsjektlederField?.label,
                                nyProsjektlederField?.id,
                            )}
                            error={
                                nyProsjektlederFormikMeta.touched &&
                                Boolean(nyProsjektlederFormikMeta.error)
                            }
                        >
                            {data?.map((v) => (
                                <MenuItem key={v?.id} value={v?.id}>
                                    {v?.value}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </Loader>
            ) : null}
        </Box>
    );
};
